/* What has happened to this definition, newest first, as the design draws it: what happened, who
 * did it, and how long ago.
 *
 * **Read from the audit chain**, so it is the record rather than a reconstruction, and it needs the
 * grant to read that chain. A reader without it is told so in one line rather than shown an empty
 * history, which would read as "nothing has happened". A scheduled run is attributed to the
 * scheduler rather than to the system account's id, which nobody can be asked about.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { can, READ_AUDIT_CHAIN_PERMISSION, useIdentity } from '@/entities/session';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { Alert } from '@/shared/ui/Alert';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useDefinitionHistory } from '../api/governance';
import { historyRows, isSystemActor } from '../model/governance';

/** How many entries the panel shows. The panel sits beside three others and the full chain is on the
 * Audit screen. */
const SHOWN = 6;

export function DefinitionHistory({
  definitionId,
}: {
  readonly definitionId: string;
}): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const readable = can(identity.data, READ_AUDIT_CHAIN_PERMISSION);
  const history = useDefinitionHistory(definitionId, { enabled: readable });
  const [now] = useState(() => new Date());

  return (
    <section className="lq-card lq-rc__panel lq-rdf__panel">
      <h3 className="lq-rc__panelTitle">{t('reconciliation.history.title')}</h3>

      {!readable && (
        <p className="lq-rdf__quiet" title={t('reconciliation.history.restricted.body')}>
          {t('reconciliation.history.restrictedShort')}
        </p>
      )}

      {readable && history.isPending && <SkeletonRows rows={4} columns={2} />}

      {readable && history.isError && (
        <Alert tone="error" title={t('reconciliation.history.failed')}>
          {history.error.message}
        </Alert>
      )}

      {readable && history.isSuccess && history.data.length === 0 && (
        <p className="lq-rdf__quiet">{t('reconciliation.history.empty')}</p>
      )}

      {readable && history.isSuccess && history.data.length > 0 && (
        <div className="lq-rdf__history">
          {historyRows(history.data)
            .slice(0, SHOWN)
            .map((row) => (
              <div key={row.key} className="lq-rdf__histRow">
                <span>
                  {t(`reconciliation.history.action.${row.action}`, {
                    defaultValue: row.action,
                  })}
                  <span className="lq-rdf__quiet">
                    {' · '}
                    {isSystemActor(row.actor)
                      ? t('reconciliation.history.scheduler')
                      : personName(row.actor)}
                  </span>
                </span>
                <span
                  className="lq-rdf__quiet lq-nowrap"
                  title={new Date(row.occurredAt).toLocaleString()}
                >
                  {ageText(t, row.occurredAt, now) ?? row.occurredAt}
                </span>
              </div>
            ))}
        </div>
      )}
    </section>
  );
}
